import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SerializedError } from "../shared/models/errorHandlingModels";
import { NotificationMessage } from '../shared/models/models';
import { Country } from '../common/models/CommonModels';
import { getCountries } from '../common/api/commonApi';
import { ConfirmRegister } from '../customer/shared/models/customerModels';
import customersApi from '../customer/shared/api/customersApi';
import { serializeError, createErrorNotification } from '../shared/utils/errorUtils';

interface ConfirmRegistrationState {
  isLoading: boolean;
  formNotificationMessage?: NotificationMessage | null;
  showPassword: boolean;
  showConfirmPassword: boolean;
  countries: Country[];
}

const initialState: ConfirmRegistrationState = {
  isLoading: false,
  showPassword: false,
  showConfirmPassword: false,
  countries: [],
};

export const loadCountries = createAsyncThunk(
  'confirmRegistration/loadCountries',
  async (_, { rejectWithValue }) => {
    try {
      const countries = await getCountries();
      return countries;
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

export const confirmRegistration = createAsyncThunk(
  'confirmRegistration/confirmRegistration',
  async (model: ConfirmRegister, { rejectWithValue }) => {
    try {
      const result = await customersApi.confirmRegister(model);
      return result;
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

const confirmRegistrationSlice = createSlice({
  name: 'confirmRegistration',
  initialState,
  reducers: {
    setFormNotificationMessage: (state, action: PayloadAction<NotificationMessage | undefined>) => {
      state.formNotificationMessage = action.payload;
    },
    togglePasswordVisibility: (state) => {
      state.showPassword = !state.showPassword;
    },
    toggleConfirmPasswordVisibility: (state) => {
      state.showConfirmPassword = !state.showConfirmPassword;
    },
    resetState: (state) => {
      state.isLoading = false;
      state.formNotificationMessage = null;
      state.showPassword = false;
      state.showConfirmPassword = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCountries.pending, (state) => {
        state.isLoading = true;
        state.formNotificationMessage = null;
      })
      .addCase(loadCountries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.countries = action.payload;
      })
      .addCase(loadCountries.rejected, (state, action) => {
        state.isLoading = false;
        state.formNotificationMessage = createErrorNotification(action.payload as SerializedError);
      })

      .addCase(confirmRegistration.pending, (state) => {
        state.isLoading = true;
        state.formNotificationMessage = null;
      })
      .addCase(confirmRegistration.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(confirmRegistration.rejected, (state, action) => {
        state.isLoading = false;
        state.formNotificationMessage = createErrorNotification(action.payload as SerializedError);
      });
  },
});

export const {
  setFormNotificationMessage,
  togglePasswordVisibility,
  toggleConfirmPasswordVisibility,
  resetState,
} = confirmRegistrationSlice.actions;

export default confirmRegistrationSlice.reducer; 